/**
 * FormInput 元件
 *
 * 提供統一的表單輸入樣式：
 * - 文字輸入
 * - 數字輸入
 * - 下拉選單
 * - 複選框
 * - 單選按鈕
 */

import { setTextContent } from '@/utils/security';

// ========================================================================
// 型別定義
// ========================================================================

export type InputType = 'text' | 'number' | 'email' | 'password';

export interface TextInputOptions {
  type?: InputType;
  placeholder?: string;
  value?: string;
  disabled?: boolean;
  required?: boolean;
  min?: number;
  max?: number;
  step?: number;
  className?: string;
  onChange?: (value: string) => void;
  /** 使用文字輸入模式來支援小數點輸入（解決 type="number" 的瀏覽器相容性問題） */
  useTextModeForDecimal?: boolean;
}

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectInputOptions {
  options: SelectOption[];
  value?: string;
  disabled?: boolean;
  className?: string;
  onChange?: (value: string) => void;
}

export interface CheckboxOptions {
  checked?: boolean;
  disabled?: boolean;
  className?: string;
  onChange?: (checked: boolean) => void;
}

// ========================================================================
// 樣式常數
// ========================================================================

const INPUT_BASE_STYLES = [
  'block',
  'w-full',
  'rounded-lg',
  'border',
  'border-gray-300',
  'px-3',
  'py-2',
  'text-sm',
  'focus:border-primary-500',
  'focus:ring-1',
  'focus:ring-primary-500',
  'focus:outline-none',
  'disabled:bg-gray-100',
  'disabled:cursor-not-allowed',
].join(' ');

const SELECT_BASE_STYLES = [
  'block',
  'w-full',
  'rounded-lg',
  'border',
  'border-gray-300',
  'px-3',
  'py-2',
  'text-sm',
  'bg-white',
  'focus:border-primary-500',
  'focus:ring-1',
  'focus:ring-primary-500',
  'focus:outline-none',
  'disabled:bg-gray-100',
  'disabled:cursor-not-allowed',
].join(' ');

const CHECKBOX_BASE_STYLES = [
  'h-4',
  'w-4',
  'rounded',
  'border-gray-300',
  'text-primary-600',
  'focus:ring-primary-500',
].join(' ');

// ========================================================================
// TextInput 類別
// ========================================================================

export class TextInput {
  private element: HTMLInputElement;
  private options: TextInputOptions;

  constructor(options: TextInputOptions = {}) {
    this.options = options;
    this.element = document.createElement('input');
    this.element.type = options.type ?? 'text';
    this.applyOptions();
    this.bindEvents();
  }

  getElement(): HTMLInputElement {
    return this.element;
  }

  getValue(): string {
    return this.element.value;
  }

  setValue(value: string): void {
    this.element.value = value;
  }

  setDisabled(disabled: boolean): void {
    this.element.disabled = disabled;
  }

  focus(): void {
    this.element.focus();
  }

  mount(container: HTMLElement): void {
    container.appendChild(this.element);
  }

  destroy(): void {
    this.element.remove();
  }

  private applyOptions(): void {
    const { placeholder, value, disabled, required, min, max, step, className } = this.options;

    this.element.className = `${INPUT_BASE_STYLES} ${className ?? ''}`;

    if (placeholder) this.element.placeholder = placeholder;
    if (value !== undefined) this.element.value = value;
    if (disabled) this.element.disabled = disabled;
    if (required) this.element.required = required;
    if (min !== undefined) this.element.min = String(min);
    if (max !== undefined) this.element.max = String(max);
    if (step !== undefined) this.element.step = String(step);
  }

  private bindEvents(): void {
    if (this.options.onChange) {
      this.element.addEventListener('input', () => {
        this.options.onChange?.(this.element.value);
      });
    }
  }
}

// ========================================================================
// NumberInput 類別
// ========================================================================

export class NumberInput {
  private element: HTMLInputElement;
  private options: TextInputOptions;
  private useTextMode: boolean;

  constructor(options: TextInputOptions = {}) {
    this.options = { ...options, type: 'number' };
    this.element = document.createElement('input');

    // ★ 所有數字輸入都使用文字模式來解決瀏覽器對小數點輸入的相容性問題
    this.useTextMode = options.useTextModeForDecimal !== false; // 預設啟用，除非明確設為 false

    // 使用 text 類型來避免瀏覽器對 number 輸入的限制
    this.element.type = 'text';
    this.element.inputMode = 'decimal';
    this.element.pattern = '[0-9]*[.]?[0-9]*';

    this.applyOptions();
    this.bindEvents();
  }

  getElement(): HTMLInputElement {
    return this.element;
  }

  getValue(): number {
    return parseFloat(this.element.value) || 0;
  }

  setValue(value: number): void {
    // 處理 NaN 和 undefined，避免顯示 "NaN"
    if (isNaN(value) || value === undefined || value === null) {
      this.element.value = '';
    } else {
      this.element.value = String(value);
    }
  }

  setDisabled(disabled: boolean): void {
    this.element.disabled = disabled;
  }

  mount(container: HTMLElement): void {
    container.appendChild(this.element);
  }

  destroy(): void {
    this.element.remove();
  }

  private applyOptions(): void {
    const { placeholder, value, disabled, required, min, max, step, className } = this.options;

    this.element.className = `${INPUT_BASE_STYLES} ${className ?? ''}`;

    if (placeholder) this.element.placeholder = placeholder;
    if (value !== undefined) this.element.value = value;
    if (disabled) this.element.disabled = disabled;
    if (required) this.element.required = required;
    if (min !== undefined) this.element.min = String(min);
    if (max !== undefined) this.element.max = String(max);
    if (step !== undefined) this.element.step = String(step);
  }

  private bindEvents(): void {
    // 文字模式下，過濾非數字字元（只允許數字和小數點）
    if (this.useTextMode) {
      // ★ 處理鍵盤輸入小數點：某些瀏覽器會阻擋 '.' 鍵，需要手動處理
      this.element.addEventListener('keydown', (e) => {
        // 檢查是否按下小數點鍵
        if (e.key === '.' || e.key === 'Period') {
          const target = e.target as HTMLInputElement;
          const currentValue = target.value;

          // 如果已經有小數點，阻止輸入
          if (currentValue.includes('.')) {
            e.preventDefault();
            return;
          }

          // 手動插入小數點到游標位置
          const start = target.selectionStart ?? currentValue.length;
          const end = target.selectionEnd ?? currentValue.length;
          const newValue = currentValue.slice(0, start) + '.' + currentValue.slice(end);

          // 更新值並設定游標位置
          target.value = newValue;
          target.setSelectionRange(start + 1, start + 1);

          // 觸發 input 事件以便 onChange 被調用
          target.dispatchEvent(new Event('input', { bubbles: true }));

          // 阻止預設行為（避免重複插入）
          e.preventDefault();
        }
      });

      this.element.addEventListener('input', (e) => {
        const target = e.target as HTMLInputElement;
        let value = target.value;

        // 移除非數字和非小數點的字元（使用 0-9 而非 \d 以確保跨瀏覽器相容性）
        value = value.replace(/[^0-9.]/g, '');

        // 確保只有一個小數點
        const parts = value.split('.');
        if (parts.length > 2) {
          value = parts[0] + '.' + parts.slice(1).join('');
        }

        // 更新輸入值
        if (target.value !== value) {
          target.value = value;
        }

        // 只有當值是有效數字且不以小數點結尾時才觸發 onChange
        // （以小數點結尾表示使用者正在輸入小數，尚未完成）
        const numValue = parseFloat(value);
        if (!isNaN(numValue) && !value.endsWith('.') && this.options.onChange) {
          this.options.onChange(value);
        }
      });
    } else if (this.options.onChange) {
      this.element.addEventListener('input', () => {
        this.options.onChange?.(this.element.value);
      });
    }
  }
}

// ========================================================================
// SelectInput 類別
// ========================================================================

export class SelectInput {
  private element: HTMLSelectElement;
  private options: SelectInputOptions;

  constructor(options: SelectInputOptions) {
    this.options = options;
    this.element = document.createElement('select');
    this.applyOptions();
    this.bindEvents();
  }

  getElement(): HTMLSelectElement {
    return this.element;
  }

  getValue(): string {
    return this.element.value;
  }

  setValue(value: string): void {
    this.element.value = value;
  }

  setDisabled(disabled: boolean): void {
    this.element.disabled = disabled;
  }

  setOptions(options: SelectOption[]): void {
    this.element.innerHTML = '';
    for (const opt of options) {
      const optionEl = document.createElement('option');
      optionEl.value = opt.value;
      optionEl.textContent = opt.label;
      this.element.appendChild(optionEl);
    }
  }

  mount(container: HTMLElement): void {
    container.appendChild(this.element);
  }

  destroy(): void {
    this.element.remove();
  }

  private applyOptions(): void {
    const { options, value, disabled, className } = this.options;

    this.element.className = `${SELECT_BASE_STYLES} ${className ?? ''}`;

    this.setOptions(options);
    if (value !== undefined) this.element.value = value;
    if (disabled) this.element.disabled = disabled;
  }

  private bindEvents(): void {
    if (this.options.onChange) {
      this.element.addEventListener('change', () => {
        this.options.onChange?.(this.element.value);
      });
    }
  }
}

// ========================================================================
// Checkbox 類別
// ========================================================================

export class Checkbox {
  private container: HTMLLabelElement;
  private input: HTMLInputElement;
  private labelText: HTMLSpanElement;
  private options: CheckboxOptions;

  constructor(label: string, options: CheckboxOptions = {}) {
    this.options = options;

    this.container = document.createElement('label');
    this.container.className = 'flex items-center gap-2 cursor-pointer';

    this.input = document.createElement('input');
    this.input.type = 'checkbox';
    this.input.className = CHECKBOX_BASE_STYLES;

    this.labelText = document.createElement('span');
    this.labelText.className = 'text-sm text-gray-700';
    this.labelText.textContent = label;

    this.container.appendChild(this.input);
    this.container.appendChild(this.labelText);

    this.applyOptions();
    this.bindEvents();
  }

  getElement(): HTMLLabelElement {
    return this.container;
  }

  isChecked(): boolean {
    return this.input.checked;
  }

  setChecked(checked: boolean): void {
    this.input.checked = checked;
  }

  setDisabled(disabled: boolean): void {
    this.input.disabled = disabled;
    this.container.classList.toggle('opacity-50', disabled);
    this.container.classList.toggle('cursor-not-allowed', disabled);
  }

  mount(container: HTMLElement): void {
    container.appendChild(this.container);
  }

  destroy(): void {
    this.container.remove();
  }

  private applyOptions(): void {
    const { checked, disabled, className } = this.options;

    if (checked) this.input.checked = checked;
    if (disabled) this.setDisabled(disabled);
    if (className) this.container.className += ` ${className}`;
  }

  private bindEvents(): void {
    if (this.options.onChange) {
      this.input.addEventListener('change', () => {
        this.options.onChange?.(this.input.checked);
      });
    }
  }
}

// ========================================================================
// FormField 包裝元件
// ========================================================================

export interface FormFieldOptions {
  label: string;
  required?: boolean;
  helpText?: string;
}

export class FormField {
  private container: HTMLDivElement;
  private labelElement: HTMLLabelElement;
  private inputContainer: HTMLDivElement;
  private helpElement: HTMLParagraphElement | null = null;

  constructor(options: FormFieldOptions) {
    this.container = document.createElement('div');
    this.container.className = 'space-y-1';

    this.labelElement = document.createElement('label');
    this.labelElement.className = 'block text-sm font-medium text-gray-700';
    // 使用 textContent 防止 XSS 攻擊
    setTextContent(this.labelElement, options.label);
    if (options.required) {
      // 安全地加入必填標記
      const asterisk = document.createElement('span');
      asterisk.className = 'text-red-500 ml-1';
      asterisk.textContent = '*';
      this.labelElement.appendChild(asterisk);
    }

    this.inputContainer = document.createElement('div');

    this.container.appendChild(this.labelElement);
    this.container.appendChild(this.inputContainer);

    if (options.helpText) {
      this.helpElement = document.createElement('p');
      this.helpElement.className = 'text-xs text-gray-500';
      this.helpElement.textContent = options.helpText;
      this.container.appendChild(this.helpElement);
    }
  }

  getElement(): HTMLDivElement {
    return this.container;
  }

  getInputContainer(): HTMLDivElement {
    return this.inputContainer;
  }

  mount(container: HTMLElement): void {
    container.appendChild(this.container);
  }

  destroy(): void {
    this.container.remove();
  }
}
